import React, { useEffect, useState } from "react";
import Login from "./Login";
import { getSession, meSession, clearSession } from "@/lib/authApi";

// Wraps the app: checks session on mount, shows Login if unauthenticated
// or if the stored session no longer verifies with the backend. Exposes
// a signOut() to children via the "authSignOut" custom event.
export default function AuthGate({ children }) {
  // Optimistic auth: if a token exists in localStorage, treat the user as
  // signed-in immediately (no visible "checking" flash). EXCEPT when Stripe
  // has just redirected the user back with a ?signup_session=... query
  // param — that means they just paid and need to see the Signup component
  // on its success step to receive their license PIN. If we let the token
  // logic run, they'd land on the dashboard (or Login) and never see the
  // PIN. So we force signed_out state whenever the returning URL carries
  // a signup_session, and Login will forward the user into Signup below.
  const [state, setState] = useState(() => {
    const returningFromStripe = (() => {
      if (typeof window === "undefined") return false;
      try {
        return new URLSearchParams(window.location.search).has("signup_session");
      } catch {
        return false;
      }
    })();
    if (returningFromStripe) {
      return { status: "signed_out", session: null };
    }
    const s = getSession();
    if (s?.token) {
      return {
        status: "signed_in",
        session: { token: s.token, license_id: s.license_id, pharmacy_name: s.pharmacy_name },
      };
    }
    return { status: "signed_out", session: null };
  });

  useEffect(() => {
    if (state.status !== "signed_in") return;
    // Silent background verification. If the stored token no longer verifies
    // (revoked/expired), clear it and drop the user back to Login. Successful
    // verifications refresh the pharmacy_name from the server (in case it
    // changed).
    meSession()
      .then((info) => {
        setState((prev) =>
          prev.status === "signed_in"
            ? {
                status: "signed_in",
                session: {
                  ...prev.session,
                  license_id: info.license_id || prev.session.license_id,
                  pharmacy_name: info.pharmacy_name || prev.session.pharmacy_name,
                },
              }
            : prev
        );
      })
      .catch(() => {
        clearSession();
        setState({ status: "signed_out", session: null });
      });
    // Intentionally empty deps — this must only run once on mount.
  }, []);

  useEffect(() => {
    const handler = () => {
      clearSession();
      setState({ status: "signed_out", session: null });
    };
    window.addEventListener("authSignOut", handler);
    return () => window.removeEventListener("authSignOut", handler);
  }, []);

  if (state.status === "signed_out") {
    return (
      <Login
        onAuthenticated={(info) => setState({
          status: "signed_in",
          session: {
            token: info.session_token || "",
            license_id: info.license_id || "",
            pharmacy_name: info.pharmacy_name || "",
          },
        })}
      />
    );
  }

  return children;
}
